import { createContext, ReactNode, useContext, useEffect, useState, useMemo } from "react";
import { AgreementType, ApplicationItemType, MessageType, ReportItemType } from "../helpers/types"
import { useParams } from "react-router-dom";
import { useAuthContext } from "./AuthProvider";
import FullscreenLoader from "../components/FullscreenLoader";
import { useConfigContext } from "./ConfigProvider";

type ApplicationContextType = {
    application: ApplicationItemType | null;
    applicationConfig: any;
    unreadMessages: number;
    messages: MessageType[] | undefined;
    setMessages: (updated: MessageType[]) => void;
    activeReports: number;
    reports: ReportItemType[] | undefined;
    agreements: AgreementType[] | undefined;
    setAgreements: (updated: AgreementType[]) => void;
    loadApplication: () => void;
    markMesageAsRead: (messageId: number) => void;
}

const ApplicationContext = createContext<ApplicationContextType | null>(null);

const useApplicationContext = () => {
    const context = useContext(ApplicationContext);
    if (context === null) {
        throw new Error('useApplicationContext must be used within a ApplicationProvider');
    }

    return context;
}

const ApplicationProvider = ({ children }: { children: ReactNode }) => {
    const { activeReportStatuses, applications } = useConfigContext();
    const { apiCall, user } = useAuthContext();

    const [application, setApplication] = useState<ApplicationItemType | null>(null);
    const [applicationConfig, setApplicationConfig] = useState<Record<string, any> | null>(null);
    const [messages, setMessages] = useState(application?.messages);
    const unreadMessages = useMemo(() => {
    if (!user || !messages) return 0;
    return messages.filter((item) => !item.read_by_user).length;
    }, [user, messages]);
    const [reports, setReports] = useState(application?.reports);
    const [activeReports, setActiveReports] = useState(0);
    const [agreements, setAgreements] = useState<AgreementType[]>([]);

    const params = useParams();

    const loadApplication = async () => {
        const response = await apiCall({
            action: 'getApplicationDetailsById',
            id: params.id,
            user_id: user?.id
        });

        if (response?.data?.status === 200) {
            setApplication(response.data.data);
            setAgreements(response.data.data?.agreements);
            // applicationConfig settes via effect basert på `applications`-listen
            // fra ConfigProvider (merget mot /rest/applicationtemplate) i stedet
            // for legacy response.data.config.applications.
        }
    }

    const setCounts = () => {
        // Reports
        let active = 0;
        if (application?.reports) {
            application?.reports.map((item) => {
                if (activeReportStatuses.includes(item.status)) active++;
            });
        }

        setActiveReports(active);
    }

    const markMessageAsRead = (messageId: number) => {
        setMessages((prev) =>
            prev?.map((m) =>
                m.id === messageId ? { ...m, read_by_user: 1 } : m
            )
        );
    };

    useEffect(() => {
        if (application) {
            setCounts()
            setMessages(application.messages);
            setReports(application.reports);
        }
    }, [application])

    useEffect(() => {
        // Slå opp riktig mal fra ConfigProvider sin samlede liste (merget mot
        // /rest/applicationtemplate). Faller tilbake til null hvis listen ikke
        // er klar ennå — så lenge applicationConfig er null vises FullscreenLoader
        // og barna rendres ikke.
        if (!application || !applications) {
            setApplicationConfig(null);
            return;
        }
        const list = Array.isArray(applications) ? applications : [];
        const match = list.find((a: any) => Number(a.type) === Number(application.type));
        setApplicationConfig(match ?? null);
    }, [application, applications]);

    useEffect(() => {
        if (params.id) {
            loadApplication()
        }
    }, [])

    return (
        <ApplicationContext.Provider value={{
            application,
            applicationConfig,
            unreadMessages,
            messages,
            setMessages,
            activeReports,
            reports,
            agreements,
            setAgreements,
            loadApplication,
            markMessageAsRead
        }}>
            {application ? children : <FullscreenLoader />}
        </ApplicationContext.Provider>
    )
}

export { ApplicationProvider, useApplicationContext }